import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Label } from '@/components/ui/shadcn/label';
import { type Setting } from '@/types';
import { RotateCcw, Save } from 'lucide-react';
import { useState } from 'react';

interface JsonEditorProps {
    setting: Setting;
    onUpdate: (key: string, value: string | number | boolean | object | null) => void;
}

export default function JsonEditor({ setting, onUpdate }: JsonEditorProps) {
    const [jsonString, setJsonString] = useState(() => {
        try {
            return JSON.stringify(setting.value, null, 2);
        } catch {
            return '[]';
        }
    });
    const [isValid, setIsValid] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const handleJsonChange = (value: string) => {
        setJsonString(value);

        try {
            JSON.parse(value);
            setIsValid(true);
            setError(null);
        } catch (e) {
            setIsValid(false);
            setError(e instanceof Error ? e.message : 'Invalid JSON');
        }
    };

    const handleSave = () => {
        if (!isValid) return;

        try {
            const parsed = JSON.parse(jsonString);
            onUpdate(setting.key, parsed);
        } catch {
            setError('Failed to parse JSON');
        }
    };

    const handleReset = () => {
        try {
            const original = JSON.stringify(setting.value, null, 2);
            setJsonString(original);
            setIsValid(true);
            setError(null);
        } catch {
            setJsonString('[]');
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">JSON Editor</CardTitle>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={handleReset}>
                            <RotateCcw className="mr-2 h-4 w-4" />
                            Reset
                        </Button>
                        <Button size="sm" onClick={handleSave} disabled={!isValid}>
                            <Save className="mr-2 h-4 w-4" />
                            Save
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                <div>
                    <Label htmlFor="json-editor">JSON Configuration</Label>
                    <div className="mt-2">
                        <textarea
                            id="json-editor"
                            value={jsonString}
                            onChange={(e) => handleJsonChange(e.target.value)}
                            className={`focus:ring-ring h-64 w-full resize-none rounded-md border p-3 font-mono text-sm focus:ring-2 focus:outline-none ${
                                !isValid ? 'border-destructive bg-danger-subtle' : 'border-border'
                            }`}
                            placeholder="Enter valid JSON..."
                        />
                    </div>
                </div>

                {error && (
                    <div className="border-danger-subtle bg-danger-subtle rounded-md border p-3">
                        <p className="text-danger-subtle-foreground text-sm">
                            <strong>Error:</strong> {error}
                        </p>
                    </div>
                )}

                {isValid && (
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="border-success text-success-subtle-foreground">
                            Valid JSON
                        </Badge>
                        <span className="text-muted-foreground text-sm">Ready to save</span>
                    </div>
                )}

                <div className="text-muted-foreground text-xs">
                    <p>
                        <strong>Tip:</strong> Use this editor for complex nested configurations.
                    </p>
                    <p>• Use proper JSON syntax with double quotes</p>
                    <p>
                        • Arrays: <code>[{`"item1", "item2"`}]</code>
                    </p>
                    <p>
                        • Objects: <code>{`{"key": "value"}`}</code>
                    </p>
                </div>
            </CardContent>
        </Card>
    );
}
